import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { IInitialState } from '../../../../reduxService';
import { IFetchTemplatesPayload } from '../../../api/common';
import * as BeMaccaferriStoreHelper from '../../../api/store/BeMaccaferriStoreHelper';
import { EProjectType, UnitOfMeasure } from '../../../enum/project';
import { IProjectInfoForm } from '../../../pages/ProjectInfo/IProjectInfo';
import { setProjectInfoTemplate } from '../../../store/projectInfo/projectInfoSlice';
import { IChannelGeometrySteppedWeirForm, IChannelGeometryVerticalDropForm } from '../../components/InputForm/ChannelGeometry/IChannelGeometry';
import { IHydraulicDataSlopedWeirForm, IHydraulicDataSteppedWeirForm, IHydraulicDataVerticalDropForm } from '../../components/InputForm/HydraulicData/IHydraulicData';
import { ISoilDataVerticalDropForm } from '../../components/InputForm/SoilData/ISoilData';
import { IStillingBasinSteppedWeirSlopedWeirForm, IStillingBasinVerticalDropForm } from '../../components/InputForm/StillingBasin/IStillingBasin';
import { IWeirGeometrySlopedWeirForm, IWeirGeometrySteppedWeirForm, IWeirGeometryVerticalDropForm } from '../../components/InputForm/WeirGeometry/IWeirGeometry';
import { IWingsGeometrySlopedWeirForm, IWingsGeometrySteppedWeirForm, IWingsGeometryVerticalDropForm } from '../../components/InputForm/WingsGeometry/IWingsGeometry';

export interface IProjectMacra2 {
    hydraulicData?: IHydraulicDataVerticalDropForm | IHydraulicDataSlopedWeirForm | IHydraulicDataSteppedWeirForm;
    geometry?: IChannelGeometryVerticalDropForm | IChannelGeometrySteppedWeirForm;
    soilData?: ISoilDataVerticalDropForm;
    stillingBasin?: IStillingBasinSteppedWeirSlopedWeirForm | IStillingBasinVerticalDropForm;
    weirGeometry?: IWeirGeometrySlopedWeirForm | IWeirGeometrySteppedWeirForm | IWeirGeometryVerticalDropForm;
    wingsGeometry?: IWingsGeometrySteppedWeirForm | IWingsGeometrySlopedWeirForm | IWingsGeometryVerticalDropForm;
}

export interface IProjectMacra2WithInfo extends IProjectMacra2 {
    projectInfo: IProjectInfoForm;
}

const initialState: IInitialState<IProjectMacra2> = {
    initialState: true,
};

export const projectSlice = createSlice({
    name: 'projectMacra2',
    initialState,
    reducers: {
        cleanProject: state => {
            state.initialState = true;
            state.data = undefined;
        },
        cleanGeometry: state => {
            state.initialState = false;
            state.data = {
                ...state.data,
                geometry: undefined,
            };
        },
        setProject: (state, action: PayloadAction<IProjectMacra2>) => {
            state.initialState = false;
            state.data = action.payload;
        },
        setHydraulicData: (state, action: PayloadAction<IHydraulicDataVerticalDropForm | IHydraulicDataSlopedWeirForm | IHydraulicDataSteppedWeirForm>) => {
            state.initialState = false;
            state.data = { ...state.data, hydraulicData: { ...action.payload, id: !!action.payload.id ? action.payload.id : state.data?.hydraulicData?.id } };
        },
        setGeometry: (state, action: PayloadAction<IChannelGeometryVerticalDropForm | IChannelGeometrySteppedWeirForm>) => {
            state.initialState = false;
            state.data = { ...state.data, geometry: action.payload };
        },
        setSoilData: (state, action: PayloadAction<ISoilDataVerticalDropForm>) => {
            state.initialState = false;
            state.data = { ...state.data, soilData: action.payload };
        },
        setStillingBasin: (state, action: PayloadAction<IStillingBasinSteppedWeirSlopedWeirForm | IStillingBasinVerticalDropForm>) => {
            state.initialState = false;
            state.data = { ...state.data, stillingBasin: action.payload };
        },
        setWeirGeometry: (state, action: PayloadAction<IWeirGeometrySlopedWeirForm | IWeirGeometrySteppedWeirForm | IWeirGeometryVerticalDropForm>) => {
            state.initialState = false;
            state.data = { ...state.data, weirGeometry: action.payload };
        },
        setWingsGeometry: (state, action: PayloadAction<IWingsGeometrySteppedWeirForm | IWingsGeometrySlopedWeirForm | IWingsGeometryVerticalDropForm>) => {
            state.initialState = false;
            state.data = { ...state.data, wingsGeometry: action.payload };
        },
    },
    extraReducers(builder) {
        builder
            .addCase(fetchTemplateById.pending, state => {
                state.data = { ...state.data };
                state.pending = true;
                state.error = undefined;
                state.initialState = false;
            })
            .addCase(fetchTemplateById.fulfilled, (state, action) => {
                state.data = { ...state.data, ...action.payload };
                state.pending = false;
                state.error = undefined;
                state.initialState = false;
            })
            .addCase(fetchTemplateById.rejected, (state, action) => {
                state.data = undefined;
                state.pending = false;
                state.error = {
                    status: (action.payload as any)?.status ?? 0,
                    data: (action.payload as any)?.data ?? 'invalid template response',
                };
                state.initialState = false;
            });
    },
});

export const fetchTemplateById = createAsyncThunk('api/Template/GetTemplateGCSById', async (payload: IFetchTemplatesPayload, { dispatch, rejectWithValue }) => {
    try {
        const { id, fromBo } = payload;
        const res = await BeMaccaferriStoreHelper.getTemplateGCSByIdAsync(id);
        if (!res.data || !res.data.geometry || !res.data.soilData || !res.data.soilData.material || !res.data.hydraulicData) {
            return rejectWithValue({ status: 0, data: 'invalid template response' });
        }
        let firstPD;
        if (!!res.data.soilData.material?.physicalData && res.data.soilData.material?.physicalData?.length > 0) firstPD = res.data.soilData.material?.physicalData[0];

        let projectGeometry: IProjectMacra2 = {
            geometry: {
                bankTopElevation: res.data.geometry.riverGeometryBankTopElevation,
                channelWidth: res.data.geometry.riverGeometryChannelWidth,
                slopeOfLeftBank: res.data.geometry.riverGeometrySlopeOfLeftBank,
                slopeOfRightBank: res.data.geometry.riverGeometrySlopeOfRightBank,
                id: res.data.geometry.id,
                materialId: res.data.geometry.materialId,
            },
            weirGeometry: {
                bottomLength: res.data.geometry.weirGeometryBottomLength,
                foundation: res.data.geometry.weirGeometryFoundation,
                foundationLayers: res.data.geometry.weirGeometryFoundationLayers,
                gabionWidth: res.data.geometry.weirGeometryGabbionWidth,
                height: res.data.geometry.weirGeometryHeight,
                layer: res.data.geometry.weirGeometryTableLayer,
                liningThickness: res.data.geometry.weirGeometryLiningThickness,
                stepHeight: res.data.geometry.weirGeometryStepHeight,
                stepNumber: res.data.geometry.weirGeometryStepNumber,
                stepRatio: res.data.geometry.weirGeometryStepRatio,
                crestLength: res.data.geometry.weirGeometryTopLenght,
            },
            wingsGeometry: {
                height: res.data.geometry.wingsGeometryHeight,
                offset: res.data.geometry.wingsGeometryOffset,
                width: res.data.geometry.wingsGeometryWidth,
                slopeAngle: res.data.geometry.wingsGeometrySlopeAngle,
            },
            stillingBasin: {
                counterweirElevation: res.data.geometry.stillingBasinCounterweirElevation,
                isCounterweir: res.data.geometry.stillingBasinIsCounterweir,
                isStillingBasin: res.data.geometry.stillingBasinIsStillinBasin,
                material: res.data.geometry.material,
                poolLength: res.data.geometry.stillingBasinPoolLength,
                stillingBasinElevation: res.data.geometry.stillingBasinElevation,
                thickness: res.data.geometry.stillingBasinThikness,
                width: res.data.geometry.stillingBasinWidth,
            },
            soilData: {
                gabionPorosity: res.data.soilData.gabionPorosity,
                coefficientSoilFoundation: res.data.soilData.coefficientSoilFoundation,
                frictionAngleSoilWeir: res.data.soilData.frictionAngleSoilWeir,
                material: res.data.soilData.material,
                rockFillUnitWeight: res.data.soilData.rockFillUnitWeight,
                underpressureInfluence: res.data.soilData.underpressureInfluence,
                granularMaterialDownstream: res.data.soilData.granularMaterialDownstream,
                id: res.data.soilData.id,
            },
            hydraulicData: res.data.hydraulicData,
        };

        if (fromBo)
            dispatch(
                setProjectInfoTemplate({
                    city: '',
                    client: '',
                    company: '',
                    country: '',
                    latitude: 0,
                    longitude: 0,
                    name: '',
                    author: '',
                    notes: '',
                    type: res.data.type | EProjectType.Macra2,
                    unitOfMeasure: UnitOfMeasure.SI,
                    imgUrl: res.data.imageUrl,
                }),
            );
        return projectGeometry;
    } catch (error: any) {
        return rejectWithValue(error.response ?? { status: 0, data: 'invalid template response' });
    }
});

export const { setHydraulicData, cleanGeometry, setProject, cleanProject, setGeometry, setSoilData, setStillingBasin, setWeirGeometry, setWingsGeometry } = projectSlice.actions;

export default projectSlice.reducer;
